/**
 * Live autoplay controller for the landing search demo (plan #4906).
 *
 * The fixture layer is gone: autoplay types scripted queries into the real
 * search widget, and every keystroke runs a real query against the seeded
 * demo instance. That is safe now because the pipeline itself carries the
 * controls the fixtures used to fake: the server caches demo results with a
 * long TTL (cache hits after the first cycle), suppresses demo analytics
 * server-side, and rate-bounds the demo instance per IP and globally.
 *
 * The controller keeps the #7964 posture the fixture autoplay established:
 * IntersectionObserver-gated, suppressed under prefers-reduced-motion and
 * hidden tabs, and once the user takes over it NEVER restarts, so autoplay
 * can never resume traffic behind a visitor's back. All the moving parts
 * (timers, gates) are injectable so the logic is testable without a browser.
 */

/**
 * The scripted autoplay queries, in order. Source of truth:
 * instant-search-ingest seeds/demo_corpus.json `scripted_queries` (the
 * canonical demo dataset). Each query returns at least 3 hits from the
 * seeded corpus, pinned there by
 * TestDemoDataset_ScriptedQueries_EachHaveMinThreeCorpusHits.
 */
export const DEMO_SCRIPTED_QUERIES = [
  'instant search',
  'Ghost search',
  'typo tolerance',
  'AI discovery',
  'search analytics',
  'content gaps',
];

/**
 * Named autoplay timings (item i22492). `standard` is the landing demo's;
 * `film` types and dwells faster so the landing film can play the whole script
 * twice, once per theme, at a sane length (doc11686). The site's
 * landing-film.ts sizes its beats from the same numbers.
 */
export const AUTOPLAY_PACES = {
  // The landing and the design showcase (item i22902) type the next query
  // over the last, as a person retypes: an emptied field shows the instance's
  // default results, which grew and reshuffled every grid between queries.
  standard: { typeMs: 100, dwellMs: 2000, betweenMs: 100, clear: false },
  film: { typeMs: 50, dwellMs: 900, betweenMs: 100 },
};

/**
 * Parse a pace pin attribute.
 *
 * @param {string|null|undefined} value
 * @returns {string|null} a non-standard AUTOPLAY_PACES name, or null
 */
export function parsePaceAttr(value) {
  const pace = String(value ?? '').trim().toLowerCase();
  return pace !== 'standard' && Object.hasOwn(AUTOPLAY_PACES, pace) ? pace : null;
}

/**
 * The timings for a pace, the standard ones for none or an unknown one.
 *
 * @param {string|null|undefined} pace
 * @returns {{typeMs: number, dwellMs: number, betweenMs: number, clear?: boolean}}
 */
export function autoplayPace(pace) {
  return { ...AUTOPLAY_PACES[parsePaceAttr(pace) ?? 'standard'] };
}

/**
 * Parse a start-query pin attribute into a script index, wrapping out-of-range
 * values onto the script.
 *
 * @param {string|null|undefined} value
 * @returns {number|null} an index into DEMO_SCRIPTED_QUERIES, or null for none
 */
export function parseStartAttr(value) {
  const text = String(value ?? '').trim();
  if (!/^-?\d+$/.test(text)) return null;
  const n = DEMO_SCRIPTED_QUERIES.length;
  return ((Number(text) % n) + n) % n;
}

/**
 * Where the next controller built without an explicit start begins in the
 * script (item #9015).
 *
 * A tab switch unmounts the demo it leaves (#8854) rather than halting it, so
 * every switch builds a fresh controller -- and every fresh controller used to
 * open on "instant search". Cycling the landing's four modes showed the same
 * query four times, which reads as a stuck demo rather than as four modes of
 * one product.
 *
 * Deterministic rather than random, on purpose: the browser test asserts a demo
 * types its query FROM ITS FIRST CHARACTER (#8842), and a random start would
 * weaken that into a much softer claim.
 */
let nextScriptStart = 0;

/**
 * Take the next starting point in the script, and move the rotation on.
 *
 * @returns {number}
 */
function takeScriptStart() {
  const start = nextScriptStart;
  nextScriptStart += 1;
  return start;
}

/**
 * The successive values the autoplay types for a query: every prefix, a
 * character at a time, so each keystroke runs a real search (item i22901).
 *
 * @param {string} query
 * @returns {string[]}
 */
export function typedPartials(query) {
  return Array.from({ length: query.length }, (_, i) => query.substring(0, i + 1));
}

/** True when the environment asks for reduced motion (autoplay stays off). */
export function prefersReducedMotion(win) {
  const w = win || (typeof window !== 'undefined' ? window : undefined);
  try {
    return !!(w && typeof w.matchMedia === 'function' && w.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch {
    return false;
  }
}

/**
 * Build the live autoplay controller. It cycles the scripted queries in
 * order, typing each one character by character through `typeQuery` (the
 * widget runs a real search per keystroke), dwelling on the results, then
 * clearing and moving on.
 *
 * @param {Object} opts
 * @param {string[]} [opts.queries] - Scripted queries (default DEMO_SCRIPTED_QUERIES)
 * @param {function(string): void} opts.typeQuery - Receives each progressive partial (e.g. "i", "in", ...)
 * @param {function(): void} [opts.clearBetween] - Clears the search between queries
 * @param {function(): boolean} [opts.isInView] - Gate: only autoplay while on screen
 * @param {function(): boolean} [opts.isHidden] - Gate: never autoplay in a hidden tab
 * @param {function(): boolean} [opts.hasSurface] - Gate: only autoplay while a search
 *   surface is mounted to receive the script (an overlay mode mounts its input
 *   on open, so before that the script would advance invisibly -- item #8842)
 * @param {boolean} [opts.reducedMotion=false] - Gate: suppress autoplay entirely
 * @param {function(Function, number): *} [opts.schedule] - Timer (default setTimeout)
 * @param {function(*): void} [opts.cancel] - Timer cancel (default clearTimeout)
 * @param {number} [opts.typeMs=100] - Delay per typed character
 * @param {number} [opts.dwellMs=2000] - Dwell on each query's results
 * @param {number} [opts.betweenMs=100] - Pause after clearing before the next query
 * @param {boolean} [opts.clear=true] - Clear the field between queries
 * @param {number} [opts.rateLimitPauseMs=5000] - Fallback pause when a rate-limit signal carries no window
 * @param {function(string, number, number): void} [opts.onQueryStart] - Called as
 *   each query begins, before its first keystroke, with the query, its script
 *   index and how long it will take (typing, dwell and clear) in ms (item i22509)
 * @param {number} [opts.startIndex] - Which scripted query this demo opens on.
 *   Defaults to the next place in the rotation, so consecutive demos -- the
 *   controllers a visitor's tab switches build -- do not all open on the same
 *   query (item #9015). Pass one to pin it.
 * @returns {{start: function(): void, stop: function(): void, pauseFor: function(number): void, handleInteraction: function(): void, isActive: function(): boolean, isPaused: function(): boolean, userTookOver: function(): boolean}}
 */
export function createLiveDemoAutoplay(opts = {}) {
  const {
    queries = DEMO_SCRIPTED_QUERIES,
    typeQuery,
    clearBetween,
    isInView = () => true,
    isHidden = () => false,
    hasSurface = () => true,
    reducedMotion = false,
    schedule = (fn, ms) => setTimeout(fn, ms),
    cancel = (id) => clearTimeout(id),
    typeMs = 100,
    dwellMs = 2000,
    betweenMs = 100,
    clear = true,
    rateLimitPauseMs = 5000,
    startIndex = takeScriptStart(),
    onQueryStart,
  } = opts;

  let active = false;
  let tookOver = false;
  let paused = false;
  // Where this demo opens. The script then runs in its written order from
  // there, so rotation changes which query a visitor arrives on, not what
  // follows it.
  let queryIndex = startIndex;
  let timer = null;

  function clearTimer() {
    if (timer != null) {
      cancel(timer);
      timer = null;
    }
  }

  function after(ms, fn) {
    timer = schedule(() => {
      timer = null;
      if (!active) return;
      fn();
    }, ms);
  }

  function typeFrom(partials, index) {
    if (index < partials.length) {
      if (typeQuery) typeQuery(partials[index]);
      after(typeMs, () => typeFrom(partials, index + 1));
      return;
    }
    // Fully typed: dwell on the results, clear (unless the pace keeps the
    // field), then continue the script.
    after(dwellMs, () => {
      if (clear && clearBetween) clearBetween();
      after(betweenMs, step);
    });
  }

  function step() {
    if (!active) return;
    const index = queryIndex % queries.length;
    const query = queries[index];
    queryIndex += 1;
    // Said first, so a look switched in answer lands with the first keystroke.
    const partials = typedPartials(query);
    if (onQueryStart) onQueryStart(query, index, partials.length * typeMs + dwellMs + betweenMs);
    typeFrom(partials, 0);
  }

  return {
    /**
     * Begin autoplay, unless running, taken over, off screen, hidden,
     * reduced-motion, or with no mounted surface to type into.
     */
    start() {
      if (active || tookOver) return;
      if (reducedMotion || isHidden() || !isInView() || !hasSurface()) return;
      active = true;
      step();
    },
    /** Pause autoplay (scrolled out of view, tab hidden); start() may resume it. */
    stop() {
      active = false;
      paused = false;
      clearTimer();
    },
    /**
     * Hold the script for a rate-limit window instead of typing into a 429
     * wall, then pick the next scripted query up where it left off. A
     * non-positive window falls back to rateLimitPauseMs.
     *
     * @param {number} ms - The server's Retry-After window in milliseconds
     */
    pauseFor(ms) {
      if (!active || tookOver) return;
      clearTimer();
      paused = true;
      const delay = ms > 0 ? ms : rateLimitPauseMs;
      timer = schedule(() => {
        timer = null;
        paused = false;
        if (!active || tookOver) return;
        step();
      }, delay);
    },
    /** Explicit user interaction: stop and never restart (real search takes over). */
    handleInteraction() {
      this.stop();
      tookOver = true;
    },
    isActive: () => active,
    isPaused: () => paused,
    userTookOver: () => tookOver,
  };
}
